import { type MoneyString, isMoneyString, parseMoney, zeroMoney } from "@ibada/shared-types/finance"

import {
  BudgetHealth,
  BudgetLifecycle,
  BudgetLineMatchMode,
  BudgetLineType,
  BudgetPeriodType,
  BudgetScope,
  BudgetStatus,
  BudgetType,
} from "@/features/budgeting/model/budget-enums"
import type {
  ActiveBudgetOverviewPayload,
  BudgetApiEnvelope,
  BudgetDetail,
  BudgetDimensionLabel,
  BudgetLine,
  BudgetLinePaceStatus,
  BudgetLineTransactionsResponse,
  BudgetRelatedTransaction,
  BudgetListFigures,
  BudgetListResponse,
  BudgetMonthActivityStatus,
  BudgetOverallPaceStatus,
  BudgetOverviewFund,
  BudgetOverviewLine,
  BudgetOverviewPayload,
  BudgetPacing,
  BudgetPerformanceSummary,
  BudgetSelectedOverviewResponse,
  BudgetSummary,
  BudgetTimePoint,
} from "@/features/budgeting/model/budget-types"
import type {
  BudgetAccountPerformance,
  BudgetFundState,
  BudgetPerformanceLine,
  BudgetPerformancePayload,
} from "@/features/budgeting/model/budget-performance-types"

function asRecord(input: unknown): Record<string, unknown> | null {
  return input && typeof input === "object" && !Array.isArray(input) ? input as Record<string, unknown> : null
}

function requiredString(value: unknown, field: string): string {
  if (typeof value === "string") return value
  throw new Error(`Invalid budget API response: ${field} is missing`)
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null
}

function requiredNumber(value: unknown, field: string): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  throw new Error(`Invalid budget API response: ${field} is missing`)
}

function nullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

/**
 * Money arrives from the API as a canonical decimal string (IBA-116) and is kept as one.
 *
 * A JSON `number` is REJECTED rather than coerced. Accepting one "for compatibility" would reintroduce the exact
 * failure this ticket removed: `9007199254740993.01` survives as a string and comes out of a float as `…994.00`,
 * a dollar gone with no error anywhere. If the server sends a number, the contract is broken and we want to know.
 */
function requiredMoney(value: unknown, field: string, currency: string): MoneyString {
  if (typeof value !== "string" || !isMoneyString(value)) {
    throw new Error(`Invalid budget API response: ${field} is not a money string`)
  }
  return parseMoney(value, currency)
}

function nullableMoney(value: unknown, currency: string): MoneyString | null {
  return typeof value === "string" && isMoneyString(value) ? parseMoney(value, currency) : null
}

function requiredCurrency(value: unknown, field: string): string {
  if (typeof value === "string" && value.length > 0) return value
  throw new Error(`Invalid budget API response: ${field} is missing`)
}

function parseEnum<TValue extends string>(
  value: unknown,
  allowed: readonly TValue[],
  field: string,
): TValue {
  if (typeof value === "string" && allowed.includes(value as TValue)) {
    return value as TValue
  }
  throw new Error(`Invalid budget API response: ${field} is invalid`)
}

export function unwrapBudgetEnvelope<TData>(input: BudgetApiEnvelope<TData>): TData {
  return input.data
}

function parseScope(value: unknown): BudgetScope {
  return parseEnum(value, Object.values(BudgetScope), "scope")
}

function parseStatus(value: unknown): BudgetStatus {
  return parseEnum(value, Object.values(BudgetStatus), "status")
}

function parseLifecycle(value: unknown): BudgetLifecycle {
  // Fall back to Draft for forward-compatibility if the backend ever omits it.
  if (typeof value === "string" && Object.values(BudgetLifecycle).includes(value as BudgetLifecycle)) {
    return value as BudgetLifecycle
  }
  return BudgetLifecycle.Draft
}

function parseType(value: unknown): BudgetType {
  return parseEnum(value, Object.values(BudgetType), "type")
}

function parsePeriodType(value: unknown): BudgetPeriodType {
  return parseEnum(value, Object.values(BudgetPeriodType), "periodType")
}

function parseLineType(value: unknown): BudgetLineType {
  return parseEnum(value, Object.values(BudgetLineType), "line.type")
}

function parseLineMatchMode(value: unknown): BudgetLineMatchMode {
  return value === undefined || value === null
    ? BudgetLineMatchMode.AccountAndFund
    : parseEnum(value, Object.values(BudgetLineMatchMode), "line.matchMode")
}

function parseHealth(value: unknown): BudgetHealth {
  if (typeof value !== "string") return BudgetHealth.Unknown
  if (Object.values(BudgetHealth).includes(value as BudgetHealth)) return value as BudgetHealth
  return BudgetHealth.Unknown
}

const PACE_STATUSES: BudgetLinePaceStatus[] = ["ahead", "behind", "on_track", "under", "over"]
const OVERALL_PACE_STATUSES: BudgetOverallPaceStatus[] = ["ahead", "behind", "on_track"]
const MONTH_ACTIVITY_STATUSES: BudgetMonthActivityStatus[] = [
  "not_started",
  "no_activity",
  "below_expected",
  "on_track",
  "above_expected",
]

function parseOverallPaceStatus(value: unknown): BudgetOverallPaceStatus {
  if (typeof value === "string" && OVERALL_PACE_STATUSES.includes(value as BudgetOverallPaceStatus)) {
    return value as BudgetOverallPaceStatus
  }
  return "on_track"
}

function parseMonthActivityStatus(value: unknown): BudgetMonthActivityStatus {
  if (typeof value === "string" && MONTH_ACTIVITY_STATUSES.includes(value as BudgetMonthActivityStatus)) {
    return value as BudgetMonthActivityStatus
  }
  return "on_track"
}

function parsePacing(input: unknown, currency: string): BudgetPacing {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: pacing is invalid")
  const monthlySnapshot = asRecord(record.monthlySnapshot)
  if (!monthlySnapshot) throw new Error("Invalid budget API response: pacing.monthlySnapshot is invalid")

  return {
    asOfDate: requiredString(record.asOfDate, "pacing.asOfDate"),
    currency,
    expectedCumulativeIncome: requiredMoney(record.expectedCumulativeIncome, "pacing.expectedCumulativeIncome", currency),
    expectedCumulativeExpenses: requiredMoney(record.expectedCumulativeExpenses, "pacing.expectedCumulativeExpenses", currency),
    actualCumulativeIncome: requiredMoney(record.actualCumulativeIncome, "pacing.actualCumulativeIncome", currency),
    actualCumulativeExpenses: requiredMoney(record.actualCumulativeExpenses, "pacing.actualCumulativeExpenses", currency),
    expectedCumulativeAmount: requiredMoney(record.expectedCumulativeAmount, "pacing.expectedCumulativeAmount", currency),
    actualCumulativeAmount: requiredMoney(record.actualCumulativeAmount, "pacing.actualCumulativeAmount", currency),
    differenceAmount: requiredMoney(record.differenceAmount, "pacing.differenceAmount", currency),
    aheadOrBehindAmount: requiredMoney(record.aheadOrBehindAmount, "pacing.aheadOrBehindAmount", currency),
    overallHealth: parseHealth(record.overallHealth),
    overallPaceStatus: parseOverallPaceStatus(record.overallPaceStatus),
    monthlySnapshot: {
      expectedNet: requiredMoney(monthlySnapshot.expectedNet, "pacing.monthlySnapshot.expectedNet", currency),
      actualNet: requiredMoney(monthlySnapshot.actualNet, "pacing.monthlySnapshot.actualNet", currency),
      differenceNet: requiredMoney(monthlySnapshot.differenceNet, "pacing.monthlySnapshot.differenceNet", currency),
    },
    currentMonthHealth: parseHealth(record.currentMonthHealth),
    currentMonthActivityStatus: parseMonthActivityStatus(record.currentMonthActivityStatus),
    aheadOrBehindMonths: nullableNumber(record.aheadOrBehindMonths),
  }
}

function parsePaceStatus(value: unknown): BudgetLinePaceStatus {
  if (typeof value === "string" && PACE_STATUSES.includes(value as BudgetLinePaceStatus)) {
    return value as BudgetLinePaceStatus
  }
  return "on_track"
}

function parseOverviewLine(input: unknown, currency: string): BudgetOverviewLine {
  const lineRecord = asRecord(input)
  if (!lineRecord) throw new Error("Invalid budget API response: overview line is invalid")

  const periodTargetAmount = requiredMoney(
    lineRecord.periodTargetAmount ?? lineRecord.plannedAmount,
    "overviewLine.periodTargetAmount",
    currency,
  )
  const expectedAmountForWindow = requiredMoney(
    lineRecord.expectedAmountForWindow ?? lineRecord.plannedAmount,
    "overviewLine.expectedAmountForWindow",
    currency,
  )
  const actualAmount = requiredMoney(lineRecord.actualAmount, "overviewLine.actualAmount", currency)
  const varianceForWindow = requiredMoney(
    lineRecord.varianceForWindow ?? lineRecord.varianceAmount,
    "overviewLine.varianceForWindow",
    currency,
  )
  const remainingForWindow = requiredMoney(
    lineRecord.remainingForWindow ?? lineRecord.remainingAmount,
    "overviewLine.remainingForWindow",
    currency,
  )

  return {
    budgetLineId: requiredString(lineRecord.budgetLineId, "overviewLine.budgetLineId"),
    periodTargetAmount,
    expectedAmountForWindow,
    actualAmount,
    // `periodTargetAmount − actualAmount` used to be computed HERE, in the browser, when the server omitted the
    // field. It never omits it now, and the browser has no business subtracting two amounts of money: the server
    // owes us the total. Missing means broken, and broken should say so.
    remainingTargetAmount: requiredMoney(
      lineRecord.remainingTargetAmount,
      "overviewLine.remainingTargetAmount",
      currency,
    ),
    remainingForWindow,
    varianceForWindow,
    variancePercentage: nullableNumber(lineRecord.variancePercentage),
    projectedFullPeriodAmount: nullableMoney(lineRecord.projectedFullPeriodAmount, currency),
    paceStatus: parsePaceStatus(lineRecord.paceStatus),
    healthStatus: parseHealth(lineRecord.healthStatus),
    plannedAmount: expectedAmountForWindow,
    varianceAmount: varianceForWindow,
    remainingAmount: remainingForWindow,
  }
}

function parseDimensionLabel(value: unknown): BudgetDimensionLabel {
  if (value === null) return null
  const record = asRecord(value)
  if (!record) return null
  const label =
    typeof record.label === "string" ? record.label
    : typeof record.name === "string" ? record.name
    : null
  if (!label) return null
  return {
    id: requiredString(record.id, "dimension.id"),
    label,
  }
}

function parseBudgetRelatedTransaction(input: unknown, currency: string): BudgetRelatedTransaction {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: transaction is invalid")

  return {
    id: requiredString(record.id, "transaction.id"),
    transactionReference: nullableString(record.transactionReference),
    type: record.type === "EXPENSE" ? "EXPENSE" : "INCOME",
    transactionKind: record.transactionKind === "REVERSAL" ? "REVERSAL" : "NORMAL",
    amount: requiredMoney(record.amount, "transaction.amount", currency),
    signedAmount: requiredMoney(record.signedAmount, "transaction.signedAmount", currency),
    occurredAt: requiredString(record.occurredAt, "transaction.occurredAt"),
    description: nullableString(record.description),
    paymentMethod:
      record.paymentMethod === "CASH" ||
      record.paymentMethod === "BANK_TRANSFER" ||
      record.paymentMethod === "MOBILE_MONEY" ||
      record.paymentMethod === "CARD" ||
      record.paymentMethod === "CHECK" ||
      record.paymentMethod === "OTHER" ?
        record.paymentMethod
      : null,
    reversesTransactionId: nullableString(record.reversesTransactionId),
    account: parseDimensionLabel(record.account),
    accountingAccount: parseDimensionLabel(record.accountingAccount),
    activity: parseDimensionLabel(record.activity),
    ministry: parseDimensionLabel(record.ministry),
    group: parseDimensionLabel(record.group),
  }
}

export function parseBudgetSummary(input: BudgetSummary): BudgetSummary {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: budget summary is invalid")
  const currency = requiredString(record.currency, "currency")

  return {
    id: requiredString(record.id, "id"),
    workspaceId: requiredString(record.workspaceId, "workspaceId"),
    branchId: nullableString(record.branchId),
    scope: parseScope(record.scope),
    type: parseType(record.type),
    activityId: nullableString(record.activityId),
    title: requiredString(record.title, "title"),
    description: nullableString(record.description),
    periodType: parsePeriodType(record.periodType),
    startDate: requiredString(record.startDate, "startDate"),
    endDate: requiredString(record.endDate, "endDate"),
    currency,
    currencyCode: currency,
    status: parseStatus(record.status),
    lifecycle: parseLifecycle(record.lifecycle),
    createdByUserId: nullableString(record.createdByUserId),
    submittedByUserId: nullableString(record.submittedByUserId),
    submittedAt: nullableString(record.submittedAt),
    approvedByUserId: nullableString(record.approvedByUserId),
    approvedAt: nullableString(record.approvedAt),
    returnedByUserId: nullableString(record.returnedByUserId),
    returnedAt: nullableString(record.returnedAt),
    reviewComment: nullableString(record.reviewComment),
    activatedByUserId: nullableString(record.activatedByUserId),
    activatedAt: nullableString(record.activatedAt),
    closedByUserId: nullableString(record.closedByUserId),
    closedAt: nullableString(record.closedAt),
    archivedAt: nullableString(record.archivedAt),
    createdAt: requiredString(record.createdAt, "createdAt"),
    updatedAt: requiredString(record.updatedAt, "updatedAt"),
    lineCount: requiredNumber(record.lineCount, "lineCount"),
    // Tolerates absence so a response from an API that predates the allow-list reads as UNCONSTRAINED, which
    // is the same thing an empty list means (IBA-157).
    allowedFunds: Array.isArray(record.allowedFunds)
      ? record.allowedFunds.flatMap((entry) => {
          const fundRecord = asRecord(entry)
          const fundId = fundRecord ? nullableString(fundRecord.fundId) : null
          return fundId ? [{ fundId, fundName: nullableString(fundRecord?.fundName) }] : []
        })
      : [],
  }
}

function parseBudgetLine(input: BudgetLine): BudgetLine {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: budget line is invalid")
  const currency = requiredCurrency(record.currency, "line.currency")
  const dimensionsRecord = asRecord(record.dimensions) ?? {}
  const dimensions = {
    incomeAccount: parseDimensionLabel(dimensionsRecord.incomeAccount),
    expenseAccount: parseDimensionLabel(dimensionsRecord.expenseAccount),
    fund: parseDimensionLabel(dimensionsRecord.fund),
  }
  const categoryLabel = dimensions.incomeAccount?.label ?? dimensions.expenseAccount?.label ?? null
  const fundLabel = dimensions.fund?.label ?? null
  const linkedItemLabel = null
  const periodsRaw = Array.isArray(record.periods) ? record.periods : []

  return {
    id: requiredString(record.id, "line.id"),
    budgetId: requiredString(record.budgetId, "line.budgetId"),
    title: requiredString(record.title, "line.title"),
    type: parseLineType(record.type),
    matchMode: parseLineMatchMode(record.matchMode),
    incomeAccountId: nullableString(record.incomeAccountId),
    expenseAccountId: nullableString(record.expenseAccountId),
    fundId: nullableString(record.fundId),
    notes: nullableString(record.notes),
    categoryLabel,
    fundLabel,
    linkedItemLabel,
    currency,
    // The DETAIL endpoint carries the plan, never the actuals — those come from the overview query and are
    // merged in later (see `mergeBudgetDetailLines`). So an actual here is not a real zero, it is UNKNOWN, and
    // `resolveActualDisplay` is what stops the UI rendering it as a hard 0. Nothing is subtracted here: with a
    // zero actual, "remaining" simply IS the plan — no browser arithmetic required, and none permitted.
    plannedAmount: requiredMoney(record.plannedAmount, "line.plannedAmount", currency),
    actualAmount: zeroMoney(currency),
    remainingAmount: requiredMoney(record.plannedAmount, "line.plannedAmount", currency),
    variance: zeroMoney(currency),
    health: parseHealth(record.health ?? record.healthStatus),
    createdAt: requiredString(record.createdAt, "line.createdAt"),
    updatedAt: requiredString(record.updatedAt, "line.updatedAt"),
    dimensions,
    periods: periodsRaw.map((period) => {
      const periodRecord = asRecord(period)
      if (!periodRecord) throw new Error("Invalid budget API response: line period is invalid")
      return {
        id: requiredString(periodRecord.id, "line.period.id"),
        periodStart: requiredString(periodRecord.periodStart, "line.period.periodStart"),
        periodEnd: requiredString(periodRecord.periodEnd, "line.period.periodEnd"),
        plannedAmount: requiredMoney(periodRecord.plannedAmount, "line.period.plannedAmount", currency),
      }
    }),
  }
}

/** A list row's figures. Tolerates absence (an API that predates them) as "no figures", never as zeros. */
export function parseBudgetListFigures(input: unknown): BudgetListFigures | null {
  const record = asRecord(input)
  if (!record) return null
  const currency = requiredCurrency(record.currency, "figures.currency")
  return {
    currency,
    plannedIncome: requiredMoney(record.plannedIncome, "figures.plannedIncome", currency),
    plannedExpenses: requiredMoney(record.plannedExpenses, "figures.plannedExpenses", currency),
    actualIncome: nullableMoney(record.actualIncome, currency),
    actualExpenses: nullableMoney(record.actualExpenses, currency),
    incomePercent: nullableNumber(record.incomePercent),
    expensesPercent: nullableNumber(record.expensesPercent),
    actualsWithheld: record.actualsWithheld === true,
  }
}

export function parseBudgetListPayload(input: BudgetListResponse): BudgetListResponse {
  const record = asRecord(input)
  if (!record || !Array.isArray(record.items)) {
    throw new Error("Invalid budget API response: budget list is invalid")
  }

  return {
    items: record.items.map((item) => ({
      ...parseBudgetSummary(item as BudgetSummary),
      figures: parseBudgetListFigures(asRecord(item)?.figures),
    })),
    total: requiredNumber(record.total, "total"),
    page: requiredNumber(record.page, "page"),
    limit: requiredNumber(record.limit, "limit"),
  }
}

export function parseBudgetDetail(input: BudgetDetail): BudgetDetail {
  const record = asRecord(input)
  if (!record || !Array.isArray(record.lines)) {
    throw new Error("Invalid budget API response: budget detail is invalid")
  }

  const optionalName = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value : null)

  return {
    ...parseBudgetSummary(input),
    lines: record.lines.map((line) => parseBudgetLine(line as BudgetLine)),
    viewerActions: Array.isArray(record.viewerActions) ? (record.viewerActions as string[]) : [],
    viewerCanEdit: typeof record.viewerCanEdit === "boolean" ? record.viewerCanEdit : false,
    viewerCanApprove: typeof record.viewerCanApprove === "boolean" ? record.viewerCanApprove : false,
    submittedByName: optionalName(record.submittedByName),
    submittedByAvatarUrl: optionalName(record.submittedByAvatarUrl),
    returnedByName: optionalName(record.returnedByName),
    returnedByAvatarUrl: optionalName(record.returnedByAvatarUrl),
    approvedByName: optionalName(record.approvedByName),
    approvedByAvatarUrl: optionalName(record.approvedByAvatarUrl),
  }
}

function parseTimePoint(input: unknown): BudgetTimePoint {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: series point is invalid")
  return {
    period: requiredString(record.period, "series.period"),
    planned: requiredNumber(record.planned, "series.planned"),
    actual: requiredNumber(record.actual, "series.actual"),
  }
}

/**
 * A per-budget performance summary. The CURRENCY comes from the payload — the budget states what it is
 * denominated in, and there is no `"USD"` default anywhere in this file: a default currency is how a Congolese
 * franc ends up with a dollar sign in front of it.
 */
function parseBudgetPerformanceSummary(input: unknown): BudgetPerformanceSummary {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: budget performance summary is invalid")
  const budgetId = requiredString(record.budgetId, "budgetSummary.budgetId")
  const currency = requiredCurrency(record.currency, "budgetSummary.currency")
  const pacing = parsePacing(record.pacing, currency)
  const healthStatus = parseHealth(record.healthStatus ?? pacing.overallHealth)
  const netVariance = requiredMoney(record.netVariance, "budgetSummary.netVariance", currency)

  return {
    budgetId,
    id: budgetId,
    title: requiredString(record.title, "budgetSummary.title"),
    lifecycle: parseLifecycle(record.lifecycle),
    startDate: requiredString(record.startDate, "budgetSummary.startDate"),
    endDate: requiredString(record.endDate, "budgetSummary.endDate"),
    healthStatus,
    health: healthStatus,
    plannedIncome: requiredMoney(record.plannedIncome, "budgetSummary.plannedIncome", currency),
    actualIncome: requiredMoney(record.actualIncome, "budgetSummary.actualIncome", currency),
    plannedExpenses: requiredMoney(record.plannedExpenses, "budgetSummary.plannedExpenses", currency),
    actualExpenses: requiredMoney(record.actualExpenses, "budgetSummary.actualExpenses", currency),
    plannedNet: requiredMoney(record.plannedNet, "budgetSummary.plannedNet", currency),
    actualNet: requiredMoney(record.actualNet, "budgetSummary.actualNet", currency),
    netVariance,
    variance: netVariance,
    currencyCode: currency,
    updatedAt: null,
    status: BudgetStatus.InProgress,
    pacing,
  }
}

function budgetNeedsAttention(summary: BudgetPerformanceSummary): boolean {
  return (
    summary.pacing.overallHealth !== BudgetHealth.Healthy ||
    summary.pacing.overallPaceStatus === "behind"
  )
}

export function parseBudgetOverviewPayload(input: BudgetSelectedOverviewResponse): BudgetOverviewPayload {
  const record = asRecord(input)
  if (!record || !Array.isArray(record.lines)) {
    throw new Error("Invalid budget API response: budget overview is invalid")
  }
  const currency = requiredCurrency(record.currency, "currency")

  return {
    budgetId: requiredString(record.budgetId, "budgetId"),
    currency,
    lifecycle: parseLifecycle(record.lifecycle),
    daysUntilStart: nullableNumber(record.daysUntilStart) ?? 0,
    daysElapsed: nullableNumber(record.daysElapsed) ?? 0,
    daysRemaining: nullableNumber(record.daysRemaining) ?? 0,
    totalDays: nullableNumber(record.totalDays) ?? 0,
    includedBudgetIds: [requiredString(record.budgetId, "budgetId")],
    reportingPeriod: {
      from: requiredString(asRecord(record.reportingPeriod)?.from, "reportingPeriod.from"),
      to: requiredString(asRecord(record.reportingPeriod)?.to, "reportingPeriod.to"),
    },
    series: Array.isArray(record.series) ? record.series.map(parseTimePoint) : [],
    lines: record.lines.map((line) => parseOverviewLine(line, currency)),
    budgetSummaries: [],
    atRisk: [],
    critical: [],
    recentlyActive: [],
    warnings: [],
    totals: {
      periodTargetIncome: requiredMoney(record.periodTargetIncome, "periodTargetIncome", currency),
      periodTargetExpenses: requiredMoney(record.periodTargetExpenses, "periodTargetExpenses", currency),
      expectedIncomeForWindow: requiredMoney(record.expectedIncomeForWindow, "expectedIncomeForWindow", currency),
      expectedExpensesForWindow: requiredMoney(
        record.expectedExpensesForWindow,
        "expectedExpensesForWindow",
        currency,
      ),
      plannedIncome: requiredMoney(record.plannedIncome, "plannedIncome", currency),
      actualIncome: requiredMoney(record.actualIncome, "actualIncome", currency),
      plannedExpenses: requiredMoney(record.plannedExpenses, "plannedExpenses", currency),
      actualExpenses: requiredMoney(record.actualExpenses, "actualExpenses", currency),
      netVariance: requiredMoney(record.netVariance, "netVariance", currency),
      remainingTargetIncome: requiredMoney(record.remainingTargetIncome, "remainingTargetIncome", currency),
      remainingTargetExpenses: requiredMoney(record.remainingTargetExpenses, "remainingTargetExpenses", currency),
      periodTargetNet: requiredMoney(record.periodTargetNet, "periodTargetNet", currency),
      remainingTargetNet: requiredMoney(record.remainingTargetNet, "remainingTargetNet", currency),
      plannedNet: requiredMoney(record.plannedNet, "plannedNet", currency),
      actualNet: requiredMoney(record.actualNet, "actualNet", currency),
      health: parseHealth(asRecord(record.pacing)?.overallHealth ?? record.healthStatus),
    },
    pacing: parsePacing(record.pacing, currency),
    funds: Array.isArray(record.funds) ? record.funds.map((fund) => parseOverviewFund(fund, currency)) : [],
  }
}

function parseOverviewFund(input: unknown, currency: string): BudgetOverviewFund {
  const fund = asRecord(input)
  if (!fund) throw new Error("Invalid budget API response: overview fund is invalid")
  return {
    fundId: requiredString(fund.fundId, "fund.fundId"),
    fundName: nullableString(fund.fundName),
    plannedIncome: requiredMoney(fund.plannedIncome, "fund.plannedIncome", currency),
    actualIncome: requiredMoney(fund.actualIncome, "fund.actualIncome", currency),
    plannedExpenses: requiredMoney(fund.plannedExpenses, "fund.plannedExpenses", currency),
    actualExpenses: requiredMoney(fund.actualExpenses, "fund.actualExpenses", currency),
    available: nullableMoney(fund.available, currency),
  }
}

export function parseActiveBudgetOverviewPayload(input: ActiveBudgetOverviewPayload): BudgetOverviewPayload {
  const record = asRecord(input)
  if (!record || !Array.isArray(record.budgetSummaries)) {
    throw new Error("Invalid budget API response: active budget overview is invalid")
  }

  const currency = requiredCurrency(record.currency, "currency")
  const pacing = parsePacing(record.pacing, currency)
  const summaries = record.budgetSummaries.map((summary) => parseBudgetPerformanceSummary(summary))
  const attentionBudgets = summaries.filter(budgetNeedsAttention)

  return {
    currency,
    includedBudgetIds: Array.isArray(record.includedBudgetIds)
      ? record.includedBudgetIds.map((id) => requiredString(id, "includedBudgetIds"))
      : [],
    reportingPeriod: {
      from: requiredString(asRecord(record.reportingPeriod)?.from, "reportingPeriod.from"),
      to: requiredString(asRecord(record.reportingPeriod)?.to, "reportingPeriod.to"),
    },
    series: Array.isArray(record.series) ? record.series.map(parseTimePoint) : [],
    lines: [],
    budgetSummaries: summaries,
    atRisk: attentionBudgets.filter((summary) => summary.healthStatus === BudgetHealth.Warning),
    critical: attentionBudgets.filter((summary) => summary.healthStatus === BudgetHealth.Critical),
    recentlyActive: summaries.slice(0, 5),
    warnings: [],
    totals: {
      periodTargetIncome: requiredMoney(record.periodTargetIncome, "periodTargetIncome", currency),
      periodTargetExpenses: requiredMoney(record.periodTargetExpenses, "periodTargetExpenses", currency),
      expectedIncomeForWindow: requiredMoney(record.expectedIncomeForWindow, "expectedIncomeForWindow", currency),
      expectedExpensesForWindow: requiredMoney(
        record.expectedExpensesForWindow,
        "expectedExpensesForWindow",
        currency,
      ),
      plannedIncome: requiredMoney(record.plannedIncome, "plannedIncome", currency),
      actualIncome: requiredMoney(record.actualIncome, "actualIncome", currency),
      plannedExpenses: requiredMoney(record.plannedExpenses, "plannedExpenses", currency),
      actualExpenses: requiredMoney(record.actualExpenses, "actualExpenses", currency),
      netVariance: requiredMoney(record.netVariance, "netVariance", currency),
      remainingTargetIncome: requiredMoney(record.remainingTargetIncome, "remainingTargetIncome", currency),
      remainingTargetExpenses: requiredMoney(record.remainingTargetExpenses, "remainingTargetExpenses", currency),
      periodTargetNet: requiredMoney(record.periodTargetNet, "periodTargetNet", currency),
      remainingTargetNet: requiredMoney(record.remainingTargetNet, "remainingTargetNet", currency),
      plannedNet: requiredMoney(record.plannedNet, "plannedNet", currency),
      actualNet: requiredMoney(record.actualNet, "actualNet", currency),
      health: parseHealth(record.healthStatus ?? pacing.overallHealth),
    },
    pacing,
  }
}

export function parseBudgetLineTransactionsPayload(input: BudgetLineTransactionsResponse): BudgetLineTransactionsResponse {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: line transactions payload is invalid")
  const reportingPeriodRecord = asRecord(record.reportingPeriod)
  if (!reportingPeriodRecord) {
    throw new Error("Invalid budget API response: line transactions reporting period is invalid")
  }
  const currency = requiredCurrency(record.currency, "currency")
  const items = Array.isArray(record.items)
    ? record.items.map((item) => parseBudgetRelatedTransaction(item, currency))
    : []

  return {
    budgetId: requiredString(record.budgetId, "budgetId"),
    budgetLineId: record.budgetLineId === null ? null : requiredString(record.budgetLineId, "budgetLineId"),
    currency,
    reportingPeriod: {
      from: requiredString(reportingPeriodRecord.from, "reportingPeriod.from"),
      to: requiredString(reportingPeriodRecord.to, "reportingPeriod.to"),
    },
    items,
    total: requiredNumber(record.total, "total"),
    page: requiredNumber(record.page, "page"),
    limit: requiredNumber(record.limit, "limit"),
  }
}

/* -------------------------------------------------------------------------- */
/* IBA-157 — performance matrix                                               */
/* -------------------------------------------------------------------------- */

function parseFundState(input: unknown, currency: string): BudgetFundState | null {
  const record = asRecord(input)
  if (!record) return null
  const fundId = nullableString(record.fundId)
  if (!fundId) return null
  return {
    fundId,
    allocated: requiredMoney(record.allocated, "fund.allocated", currency),
    spent: requiredMoney(record.spent, "fund.spent", currency),
    reserved: requiredMoney(record.reserved, "fund.reserved", currency),
    balance: requiredMoney(record.balance, "fund.balance", currency),
    available: requiredMoney(record.available, "fund.available", currency),
  }
}

function parseAccountPerformance(input: unknown, currency: string): BudgetAccountPerformance | null {
  const record = asRecord(input)
  if (!record) return null
  const accountId = nullableString(record.accountId)
  if (!accountId) return null
  return {
    accountId,
    accountName: nullableString(record.accountName),
    planned: requiredMoney(record.planned, "planned", currency),
    actual: requiredMoney(record.actual, "actual", currency),
    variance: requiredMoney(record.variance, "variance", currency),
    utilizationPct: nullableNumber(record.utilizationPct),
    healthStatus: parseHealth(record.healthStatus),
  }
}

function parsePerformanceLine(input: unknown, currency: string): BudgetPerformanceLine | null {
  const record = asRecord(input)
  if (!record) return null
  const budgetLineId = nullableString(record.budgetLineId)
  if (!budgetLineId) return null
  return {
    budgetId: requiredString(record.budgetId, "budgetId"),
    budgetTitle: nullableString(record.budgetTitle) ?? "",
    budgetLineId,
    lineTitle: nullableString(record.lineTitle) ?? "",
    type: parseLineType(record.type),
    accountId: nullableString(record.accountId),
    accountName: nullableString(record.accountName),
    fundId: nullableString(record.fundId),
    fundName: nullableString(record.fundName),
    planned: requiredMoney(record.planned, "planned", currency),
    actual: requiredMoney(record.actual, "actual", currency),
    variance: requiredMoney(record.variance, "variance", currency),
    utilizationPct: nullableNumber(record.utilizationPct),
    healthStatus: parseHealth(record.healthStatus),
    fund: parseFundState(record.fund, currency),
  }
}

export function parseBudgetPerformancePayload(input: unknown): BudgetPerformancePayload {
  const record = asRecord(input)
  if (!record) throw new Error("Invalid budget API response: performance payload is invalid")
  const currency = requiredCurrency(record.currency, "currency")
  const period = asRecord(record.reportingPeriod) ?? {}
  const list = <T>(value: unknown, parse: (entry: unknown) => T | null): T[] =>
    Array.isArray(value) ? value.flatMap((entry) => { const parsed = parse(entry); return parsed ? [parsed] : [] }) : []

  return {
    workspaceId: requiredString(record.workspaceId, "workspaceId"),
    branchId: requiredString(record.branchId, "branchId"),
    context: parseScope(record.context),
    currency,
    reportingPeriod: {
      from: nullableString(period.from) ?? "",
      to: nullableString(period.to) ?? "",
    },
    includedBudgetIds: Array.isArray(record.includedBudgetIds)
      ? record.includedBudgetIds.flatMap((id) => { const value = nullableString(id); return value ? [value] : [] })
      : [],
    byIncomeAccount: list(record.byIncomeAccount, (entry) => parseAccountPerformance(entry, currency)),
    byExpenseAccount: list(record.byExpenseAccount, (entry) => parseAccountPerformance(entry, currency)),
    byFund: list(record.byFund, (entry) => {
      const fundRecord = asRecord(entry)
      const fundId = fundRecord ? nullableString(fundRecord.fundId) : null
      if (!fundRecord || !fundId) return null
      return {
        fundId,
        fundName: nullableString(fundRecord.fundName),
        planned: requiredMoney(fundRecord.planned, "planned", currency),
        actual: requiredMoney(fundRecord.actual, "actual", currency),
        utilizationPct: nullableNumber(fundRecord.utilizationPct),
        fund: parseFundState(fundRecord.fund, currency),
      }
    }),
    lines: list(record.lines, (entry) => parsePerformanceLine(entry, currency)),
    overspent: list(record.overspent, (entry) => parsePerformanceLine(entry, currency)),
  }
}
